import { NumberInput, type NumberInputProps, SegmentedControl, Text } from '@mantine/core'
import { useState } from 'react'

export type Unit = 'ft' | 'm'

const FT_PER_METER = 3.28084

interface Props extends Omit<NumberInputProps, 'value' | 'onChange'> {
  valueMeters: number | null | undefined
  onChangeMeters: (meters: number | null) => void
  defaultUnit?: Unit
  showConversion?: boolean
}

/**
 * Number input with ft/m unit toggle. Value is stored in meters.
 * Display auto-converts when unit changes. Matches Metarina boat-create UI.
 */
export function DualUnitInput({
  valueMeters,
  onChangeMeters,
  defaultUnit = 'ft',
  showConversion = true,
  ...rest
}: Props) {
  const [unit, setUnit] = useState<Unit>(defaultUnit)
  const displayValue = toDisplay(valueMeters, unit)
  const conversionLabel = getConversionLabel(valueMeters, unit)

  const handleChange = (v: number | string) => {
    if (v === '' || v === null || v === undefined) {
      onChangeMeters(null)
      return
    }
    const num = typeof v === 'string' ? Number(v) : v
    if (Number.isNaN(num)) return
    const meters = unit === 'ft' ? num / FT_PER_METER : num
    onChangeMeters(Number(meters.toFixed(3)))
  }

  return (
    <NumberInput
      {...rest}
      value={displayValue}
      onChange={handleChange}
      leftSection={
        <SegmentedControl
          size="xs"
          value={unit}
          onChange={(v) => setUnit(v as Unit)}
          data={[
            { label: 'ft', value: 'ft' },
            { label: 'm', value: 'm' },
          ]}
          styles={{ root: { border: 'none', background: 'transparent' } }}
        />
      }
      leftSectionWidth={80}
      rightSection={
        showConversion && conversionLabel ? (
          <Text size="xs" c="dimmed" pr="xs">
            {conversionLabel}
          </Text>
        ) : undefined
      }
      rightSectionWidth={showConversion ? 80 : undefined}
    />
  )
}

function toDisplay(meters: number | null | undefined, unit: Unit): number | string {
  if (meters === null || meters === undefined) return ''
  return unit === 'ft' ? Number((meters * FT_PER_METER).toFixed(2)) : Number(meters.toFixed(2))
}

function getConversionLabel(meters: number | null | undefined, unit: Unit): string {
  if (meters === null || meters === undefined || meters === 0) return unit === 'ft' ? '0 m' : '0 ft'
  if (unit === 'ft') return `${meters.toFixed(1)} m`
  return `${(meters * FT_PER_METER).toFixed(1)} ft`
}

export { FT_PER_METER }
